// reCAPTCHA v2, for the one screen that needs it.
//
// Upstream's JSP loads `https://www.google.com/recaptcha/api.js` for the whole page and
// `sign-up.page.tpl.html` just leaves a `<div id="g-recaptcha-container">` for it to fill.
// There is no JSP any more, so the script is loaded on demand by the only route that uses
// it — which also keeps a third-party script off every other screen.
//
// The site key comes from `/extension/view-model` (`recaptchaSiteKey`), not from a build
// flag: it differs per tenant, and a wrong key fails validation server-side rather than
// visibly in the widget.

export interface Recaptcha {
  render: (container: HTMLElement, options: { sitekey: string }) => number
  getResponse: (widgetId: number) => string
  reset: (widgetId: number) => void
  /**
   * ⚠️ THE GATE, AND LEAVING IT OUT COST THE WHOLE SCREEN. See `loadRecaptcha`: it is
   * present on the stub `api.js` defines immediately, while `render` is not.
   */
  ready: (callback: () => void) => void
}

declare global {
  interface Window {
    grecaptcha?: Recaptcha
  }
}

const SRC = 'https://www.google.com/recaptcha/api.js?render=explicit'

let pending: Promise<Recaptcha> | undefined

/**
 * Resolves once `window.grecaptcha` is usable. Idempotent: several mounts share one script
 * tag and one promise, because injecting the script twice re-initialises the widget registry
 * and orphans any widget already rendered.
 */
export async function loadRecaptcha(): Promise<Recaptcha> {
  // ⚠️ `window.grecaptcha` EXISTING IS NOT THE SAME AS `render` EXISTING, and the previous
  // version of this file said so in a comment and then resolved anyway. `api.js` defines a
  // STUB synchronously — `ready` and little else — and fetches `recaptcha__en.js`, which is
  // what adds `render`. Resolving on the script's `load` event therefore hands the caller an
  // object whose `render` is `undefined`, `grecaptcha.render(...)` throws TypeError, and
  // `SignUpRoute` catches it so a blocked third-party script cannot take the form down.
  //
  // The result: on any tenant that actually validates captcha, the widget never appeared and
  // sign-up posted an empty `gRecaptchaResponse` — which the server rejects. The screen was
  // measured for months against a tenant with `captchaValidationDisabled: true`, where no
  // widget is correct, so nothing ever rendered it and nothing ever failed. Found the day
  // that flag was switched on.
  //
  // `grecaptcha.ready(cb)` is the documented gate and queues on the stub, so it is correct
  // both before and after the second script lands.
  pending ??= new Promise<Recaptcha>((resolve, reject) => {
    const whenReady = (api: Recaptcha): void => {
      if (typeof api.ready !== 'function') {
        // Older builds of api.js with no `ready`: `render` is all there is, so take it.
        resolve(api)
        return
      }
      api.ready(() => {
        resolve(window.grecaptcha ?? api)
      })
    }

    const existing = window.grecaptcha
    if (existing !== undefined) {
      whenReady(existing)
      return
    }

    const script = document.createElement('script')
    script.src = SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => {
      const api = window.grecaptcha
      if (api === undefined) {
        reject(new Error('recaptcha loaded without defining window.grecaptcha'))
        return
      }
      whenReady(api)
    })
    script.addEventListener('error', () => {
      pending = undefined
      reject(new Error(`failed to load ${SRC}`))
    })
    document.head.append(script)
  })
  return pending
}
